import { css } from '@emotion/react'
import {
  Box,
  Button,
  Checkbox,
  GRangeFilterPrice,
  HStack,
  Text,
} from '@gazin/ecommerce-ui'
import {
  Dispatch,
  FormEventHandler,
  memo,
  SetStateAction,
  useMemo,
  useState,
} from 'react'
import { Filter, SelectedFilters } from 'src/services/searchProducts/dtos'
import { FilterProduct } from 'src/templates/SearchAndCategory'
import { getFilteredPrice, minimumAndMaximumPrice } from 'src/util/products'
import { Accordion } from '../accordion'

interface FilterProductMobileProps {
  filter: Filter
  isFilteringMobile?: boolean
  keyFilter: number
  appliedFilters?: SelectedFilters[]
  selectedFilters: Partial<FilterProduct>[]
  setSelectedFilters: Dispatch<SetStateAction<Partial<FilterProduct>[]>>
}

function FilterProductMobile({
  filter,
  isFilteringMobile = false,
  keyFilter,
  appliedFilters,
  selectedFilters,
  setSelectedFilters,
}: FilterProductMobileProps) {
  const [showMore, setShowMore] = useState(false)

  function resetScroll() {
    const filterScroll = document.querySelector(`.filter-scroll-${keyFilter}`)

    filterScroll?.scroll(0, 0)
  }

  const treatedFiltersValues = useMemo(() => {
    const MAX_FILTERS = 5
    const fullFilters = filter.values.length
    const filters = filter.values.slice(0, showMore ? fullFilters : MAX_FILTERS)

    return filters.map((item) => {
      const itemIdAtributo = item?.idO
      const itemIdValor = String(item?.id ?? '')
      const isSelected = selectedFilters.some(
        ({ id_atributo, id_valor_unidade }) =>
          id_atributo === itemIdAtributo && id_valor_unidade === itemIdValor
      )

      return { ...item, selected: isSelected }
    })
  }, [filter.values, selectedFilters, showMore])

  function handleToggleSelectedFiltersMobile(
    id_atributo: number | string,
    id_valor_unidade: number | string,
    tipo: string,
    valor_minimo: number | undefined,
    valor_maximo: number | undefined,
    attribute: string,
    label: string
  ) {
    const TYPE_CONTINUOUS = 'continuous'

    const filterData = {
      id_atributo,
      id_valor_unidade,
      tipo,
      valor_minimo,
      valor_maximo,
      attribute,
      label,
    }

    if (!selectedFilters.length) {
      return setSelectedFilters((prevSelectedFilters) => [
        ...prevSelectedFilters,
        filterData,
      ])
    }

    if (tipo !== TYPE_CONTINUOUS) {
      return setSelectedFilters((prev) => {
        const alreadySelected = prev.some(
          (item) =>
            item.id_atributo === id_atributo &&
            item.id_valor_unidade === id_valor_unidade
        )

        if (!alreadySelected) return [...prev, filterData]

        return prev.filter(
          (item) =>
            !(
              item.id_atributo === id_atributo &&
              item.id_valor_unidade === id_valor_unidade
            )
        )
      })
    }

    const indexFilterWithIdAtributoAndTipoContinuous =
      selectedFilters.findIndex(
        (item) =>
          item?.id_atributo === id_atributo && item?.tipo === TYPE_CONTINUOUS
      )

    const notFoundIndex = indexFilterWithIdAtributoAndTipoContinuous === -1
    if (notFoundIndex) {
      return setSelectedFilters((prevSelectedFilters) => [
        ...prevSelectedFilters,
        filterData,
      ])
    }

    setSelectedFilters((prevSelectedFilters) => {
      const newPriceObject = {
        ...prevSelectedFilters[indexFilterWithIdAtributoAndTipoContinuous],
        valor_minimo,
        valor_maximo,
      }

      prevSelectedFilters.splice(indexFilterWithIdAtributoAndTipoContinuous, 1)
      return [...prevSelectedFilters, newPriceObject]
    })
  }
  const filteredPrice = getFilteredPrice(appliedFilters)
  const { min: priceMin, max: priceMax } = minimumAndMaximumPrice(filter.values)

  if (filter.attribute === 'Preço' && (!priceMin || !priceMax)) return null

  return (
    <>
      <Box
        alignItems="flex-start"
        justifyContent="space-between"
        minW="100%"
        h="100%"
        key={keyFilter}
      >
        <Accordion.Item title={filter?.attribute}>
          {filter?.attribute === 'Preço' ? (
            <Box maxW="95%" margin="0 auto">
              <GRangeFilterPrice
                minimumPrice={
                  filteredPrice?.min ||
                  minimumAndMaximumPrice(filter.values).min
                }
                maximumPrice={
                  filteredPrice?.max ||
                  minimumAndMaximumPrice(filter.values).max
                }
                onChange={
                  ((minimum: number, maximum: number) => {
                    filter?.attribute === 'Preço' &&
                      filter?.values?.map((item: any) => {
                        if (isFilteringMobile && item?.idO) {
                          handleToggleSelectedFiltersMobile(
                            item.idO,
                            // contínuo (preço): posição do unityId no contrato c:facet:unityId:min:max
                            String(item?.unityId ?? item?.id ?? ''),
                            filter.type,
                            minimum,
                            maximum,
                            filter.attribute,
                            item?.label ?? ''
                          )
                        }
                      })
                  }) as ((minimum: number, maximum: number) => void) &
                    FormEventHandler<HTMLDivElement>
                }
                css={css`
                  p {
                    display: none;
                  }
                `}
              />
            </Box>
          ) : (
            <>
              <Box
                maxH={`${showMore ? '100%' : '7.5rem'}`}
                h="100%"
                overflow={`${showMore ? 'auto' : 'hidden'}`}
                css={css`
                  &::-webkit-scrollbar {
                    display: none;
                  }
                `}
                className={`filter-scroll-${keyFilter}`}
              >
                {treatedFiltersValues?.map((item: any) => {
                  return (
                    <HStack
                      alignItems="center"
                      justifyContent="space-between"
                      key={item.id}
                      pl="1.5rem"
                    >
                      <HStack>
                        <Checkbox
                          borderColor="neutral.200"
                          onChange={() => {
                            if (!item?.idO) return

                            handleToggleSelectedFiltersMobile(
                              item.idO,
                              String(item?.id ?? ''),
                              filter.type,
                              item?.min?.value,
                              item?.max?.value,
                              filter.attribute,
                              item?.label ?? ''
                            )
                          }}
                          isChecked={item.selected}
                        />
                        <Text
                          color="neutral.700"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontFamily="body"
                        >
                          {item?.label}
                        </Text>
                      </HStack>
                    </HStack>
                  )
                })}
              </Box>
              <Button
                variant="ghost"
                color="primary.500"
                fontSize="0.875rem"
                mt="1rem"
                fontWeight="600"
                lineHeight="1.05rem"
                _hover={{ bg: 'transparent' }}
                h="fit-content"
                px={0}
                alignItems="flex-start"
                justifyContent="flex-start"
                display={`${filter?.values?.length > 5 ? 'flex' : 'none'}`}
                onClick={() => {
                  resetScroll()
                  setShowMore(!showMore)
                }}
              >
                {showMore ? 'Ver menos' : 'Ver tudo'}
              </Button>
            </>
          )}
        </Accordion.Item>
      </Box>
    </>
  )
}

export default memo(FilterProductMobile, function (prevProps, nextProps) {
  const attribute = nextProps.filter.attribute
  const prevSelectedFilters = prevProps.selectedFilters
  const nextSelectedFilters = nextProps.selectedFilters

  function getQuantitySelectedFiltersByAttribute(
    selectedFilters: Partial<FilterProduct>[],
    attribute: string
  ) {
    return selectedFilters.filter(
      (item) => item.attribute === attribute || item.descricao === attribute
    ).length
  }

  return (
    getQuantitySelectedFiltersByAttribute(prevSelectedFilters, attribute) ===
      getQuantitySelectedFiltersByAttribute(nextSelectedFilters, attribute) &&
    prevProps.isFilteringMobile === nextProps.isFilteringMobile
  )
})
